import React, { useEffect, useState } from 'react';

import renderModal from '@components/show_page/modals/renderModal';

import {
  CloseButton,
  PrimaryButton,
  TextWarningButton,
} from '@components/library/button/index';
import InputContainer from '@components/library/input_container/InputContainer';
import { InputType } from '@components/library/inputs/Input.props';
import Input from '@components/library/inputs/PrimaryInput';
import Label from '@components/library/labels/PrimaryLabel';
import RadioButton from '@components/common/radio_button/main';
import FilestackUploader from '@components/common/filestack_uploader/main';

import { FileStackOnUpload } from '@components/common/filestack_uploader/FileStackTypes';
import { getPortalCard, createPortalCard, updatePortalCard } from '@api/v4/private/portal_cards';
import { PortalCardTypes, DisplayWidths, ValidationErrors } from '@api/v4/private/PortalCardTypes';
import { handleDeletion } from '../helpers/card_deletion_helper';
import { makeValidUrl } from '../helpers/make_valid_url';
import {
  handleValidations,
  ValidationSettings,
  nameError,
  linkError,
  descriptionError
} from '../portal_cards/validations';

interface TileFormProps {
  closeModal: () => void;
  portalCardId?: string;
  triggerCardsRefresh: () => void;
}

const TileForm: React.FunctionComponent<TileFormProps> = ({
  closeModal,
  portalCardId,
  triggerCardsRefresh
}): JSX.Element => {
  const type = PortalCardTypes.PortalTile;
  const [imageUrl, setImageUrl] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [description, setDescription] = useState("");
  const [displayWidth, setDisplayWidth] = useState(DisplayWidths.OneThird);
  const [name, setName] = useState("");
  const [validationErrors, setValidationErrors] = useState<ValidationErrors[]>([]);
  const portalId = (BFG.resource.type === 'portal') ? BFG.resource.key : null;
  const invalidSubmission = (validationErrors.length > 0);
  const nameLength = name ? name.length : 0;
  const descriptionLength = description ? description.length : 0;

  useEffect(() => {
    if (portalCardId) {
      getPortalCard({ portalCardId })
        .then((response) => {
          const { attributes } = response.data;
          setImageUrl(attributes.image_url);
          setLinkUrl(attributes.link_url);
          setDescription(attributes.description);
          setDisplayWidth(attributes.display_width);
          setName(attributes.name);
        });
    }
  }, [portalCardId]);

  const requestBody = {
    imageUrl,
    description,
    displayWidth,
    linkUrl,
    name,
    type
  };

  const submitCreateCard = ((): void => {
    if (handleValidations(type, setValidationErrors, requestBody)) {
      return;
    }
    createPortalCard(portalId, requestBody)
      .then(() => {
        triggerCardsRefresh();
        closeModal();
      })
      .catch(() => {
        Notify.create({ title: t("Something went wrong, tile update failed."), type: "error" });
        closeModal();
      });
  });

  const submitUpdateCard = ((): void => {
    if (handleValidations(type, setValidationErrors, requestBody)) {
      return;
    }

    updatePortalCard(portalCardId, requestBody)
      .then(() => {
        triggerCardsRefresh();
        closeModal();
      })
      .catch(() => {
        Notify.create({ title: t("Something went wrong, tile update failed."), type: "error" });
        closeModal();
      });
  });

  return (
    <div className="modal-content-wrapper portal-modal tile-form">
      <div className="portal-modal__header">
        {portalCardId ? (
          <span className="bff-edit" aria-hidden="true" />
        ) : (
          <span className="bff-plus" aria-hidden="true" />
        )}
        <h3 className="modal-title">{`${portalCardId ? t("Edit") : t("Create")} ${t("Navigation Tile")}`}</h3>
        <CloseButton onClick={closeModal} />
      </div>
      <div className="portal-modal__body">
        <div className="tile-form__grid-selection radio-button-container">
          <div>
            <img
              alt="one third tile grid"
              className="tile-form__grid-selection--image"
              src="https://cdn.brandfolder.io/27C9EC93/at/q6y1hw-46gjdc-928tl1/one-third-tile.auto"
            />
            <RadioButton
              checkedButton={displayWidth}
              labelCopy={t("1/3 tile")}
              name="one-third-grid"
              onChangeFunction={(): void => setDisplayWidth(DisplayWidths.OneThird)}
              valueCopy={DisplayWidths.OneThird}
            />
          </div>
          <div>
            <img
              alt="two thirds tile grid"
              className="tile-form__grid-selection--image"
              src="https://cdn.brandfolder.io/27C9EC93/at/q6y1im-bd2kmw-7r2v38/two-thirds-tile.auto"
            />
            <RadioButton
              checkedButton={displayWidth}
              labelCopy={t("2/3 tile")}
              name="two-thirds-grid"
              onChangeFunction={(): void => setDisplayWidth(DisplayWidths.TwoThirds)}
              valueCopy={DisplayWidths.TwoThirds}
            />
          </div>
          <div>
            <img
              alt="full width tile grid"
              className="tile-form__grid-selection--image"
              src="https://cdn.brandfolder.io/27C9EC93/at/q6y1jn-7xcm1k-2t5gh7/full-width-tile.auto"
            />
            <RadioButton
              checkedButton={displayWidth}
              labelCopy={t("Full-width tile")}
              name="full-width-grid"
              onChangeFunction={(): void => setDisplayWidth(DisplayWidths.Full)}
              valueCopy={DisplayWidths.Full}
            />
          </div>
        </div>
        <div className="tile-form__name">
          <div className="portal-modal__character-limit">
            <InputContainer
              attributes={{
                className: `${ValidationSettings[displayWidth].nameLength - nameLength < 0 ? 'invalid' : ''}`,
                name: "tile-name",
                onChange: (e: InputChangeEvent): void => setName(e.target.value),
                type: InputType.Text,
              }}
              input={{ value: name, error: nameError(validationErrors) }}
              labelCopy={`${t("Name")} *`}
              submitAttempted={invalidSubmission}
            />
            <span className="portal-modal__character-limit-count">
              <span className="countdown">
                {`${ValidationSettings[displayWidth].nameLength - nameLength}`}
              </span>
              {`${t("Characters")}`}
            </span>
          </div>
        </div>
        <InputContainer
          attributes={{
            name: "tile-url",
            onChange: (e: InputChangeEvent): void => setLinkUrl(e.target.value),
            onBlur: (): void => setLinkUrl(makeValidUrl(linkUrl)),
            type: InputType.Text,
          }}
          input={{ value: linkUrl, error: linkError(validationErrors) }}
          labelCopy={`${t("Link URL")} *`}
          submitAttempted={invalidSubmission}
        />
        <div className="tile-form__description">
          <div className="portal-modal__character-limit">
            <InputContainer
              attributes={{
                className: `${ValidationSettings[displayWidth].descriptionLength - descriptionLength < 0 ? 'invalid' : ''}`,
                name: "tile-description",
                onChange: (e: React.ChangeEvent<HTMLTextAreaElement>): void => setDescription(e.target.value),
                type: InputType.Textarea,
              }}
              input={{ value: description, error: descriptionError(validationErrors) }}
              labelCopy={t("Description")}
              submitAttempted={invalidSubmission}
            />
            <span className="portal-modal__character-limit-count">
              <span className="countdown">
                {`${ValidationSettings[displayWidth].descriptionLength - descriptionLength}`}
              </span>
              {`${t("Characters")}`}
            </span>
          </div>
        </div>
        <div className="portal-modal__filestack-container">
          <div className="portal-modal__filestack-container--description">
            <h3>{t("Tile image")}</h3>
            {displayWidth === 'one-third' && (
              <p>{t("Recommended size: 300 x 220px")}</p>
            )}
            {displayWidth === 'two-thirds' && (
              <p>{t("Recommended size: 630 x 220px")}</p>
            )}
            {displayWidth === 'full' && (
              <p>{t("Recommended size: 960 x 220px")}</p>
            )}
          </div>
          {imageUrl ? (
            <div className="portal-modal__filestack-container--thumbnail">
              <div className="portal-modal__filestack-container--thumbnail-container">
                <img
                  className="portal-modal__filestack-container--thumbnail--image"
                  src={imageUrl}
                  alt="Icon"
                />
              </div>
              <TextWarningButton
                icon="bff-trash"
                onClick={(): void => setImageUrl(null)}
              />
            </div>
          )
            : (
              <FilestackUploader
                options={{
                  maxFiles: 1,
                  accept: "image/*",
                  transformations: { crop: { aspectRatio: ValidationSettings[displayWidth].aspectRatio, force: true } }
                }}
                type="area"
                onSuccess={(files: FileStackOnUpload): void => setImageUrl(files.filesUploaded[0].url)}
              />
            )
        }
        </div>
        <div className="portal-modal__button">
          {portalCardId && (
            <div className="portal-modal__two-buttons">
              <TextWarningButton
                icon="bff-trash"
                onClick={(): void => handleDeletion(portalCardId, name, closeModal, triggerCardsRefresh)}
              >
                {t("Delete Tile")}
              </TextWarningButton>
              <PrimaryButton
                onClick={submitUpdateCard}
                size="medium"
              >
                {t("Update Tile")}
              </PrimaryButton>
            </div>
          )}
          {!portalCardId && (
            <PrimaryButton
              onClick={submitCreateCard}
              size="small"
            >
              {t("Create Tile")}
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
};

const ModalComponent = renderModal(TileForm, "TileForm");
export default ModalComponent;
